import {type FC, useEffect, useRef, useState} from 'react';
import {
    ActionButton,
    ActionRow,
    Address,
    BackButton,
    Card,
    EmptyState,
    FoodInfoPageWrapper,
    InfoGrid,
    MapWrapper,
    Meta,
    Pin,
    PositionWrapper,
    Skeleton,
    Title,
} from '../types/FoodInfoPage.style.ts';
import {useNavigate} from 'react-router';
import useFoodStore from '../../../store/useFoodStore.ts';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';

const FoodInfoDetailPage: FC = () => {
    const navigate = useNavigate();
    const {selectedFoodInfo} = useFoodStore();
    const mapElement = useRef<HTMLDivElement | null>(null);
    const [mapError, setMapError] = useState<string | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    useEffect(() => {
        if (!mapElement.current) return;
        setMapError(null);
        setIsMapLoaded(false);

        const lat = selectedFoodInfo?.mapy;
        const lng = selectedFoodInfo?.mapx;

        try {
            const center =
                lat && lng
                    ? new window.naver.maps.LatLng(lat, lng)
                    : new window.naver.maps.LatLng(37.45617301, 126.7059186);

            const map = new window.naver.maps.Map(mapElement.current, {
                center,
                zoom: 16,
            });

            new window.naver.maps.Marker({
                position: center,
                map,
            });

            setIsMapLoaded(true);
        } catch (err) {
            console.error('네이버 지도 로드 실패:', err);
            setMapError('지도를 불러오는 데 실패했습니다.');
        }
    }, [selectedFoodInfo]);

    const {ref: backBtnRef, focused: backBtnFocused} = useFocusable({
        focusKey: 'food-info-detail-back-button',
        onEnterPress: () => navigate(-1),
        onArrowPress: () => {
            return false;
        },
    });

    useEffect(() => {
        setFocus('food-info-detail-back-button');
    }, []);

    if (!selectedFoodInfo) {
        return (
            <FoodInfoPageWrapper>
                <BackButton ref={backBtnRef} $focused={backBtnFocused} onClick={() => navigate(-1)}>
                    뒤로가기
                </BackButton>
                <EmptyState>선택된 음식 정보가 없습니다.</EmptyState>
            </FoodInfoPageWrapper>
        );
    }

    const telLink = selectedFoodInfo?.telephone ? `tel:${selectedFoodInfo.telephone}` : undefined;

    return (
        <FoodInfoPageWrapper>
            <BackButton ref={backBtnRef} $focused={backBtnFocused} onClick={() => navigate(-1)}>
                뒤로가기
            </BackButton>

            <InfoGrid>
                <Card>
                    <Title>{selectedFoodInfo.title ?? '제목 없음'}</Title>
                    <Meta>{selectedFoodInfo.category ?? '카테고리 정보 없음'}</Meta>

                    <PositionWrapper>
                        <Pin>📍</Pin>
                        음식점 주소
                    </PositionWrapper>

                    <Address>
                        {selectedFoodInfo.roadAddress ?? selectedFoodInfo.address ?? <Skeleton width="70%" />}
                    </Address>

                    <ActionRow>
                        <ActionButton
                            as="a"
                            href={telLink ?? '#'}
                            onClick={(e) => {
                                if (!telLink) e.preventDefault();
                            }}
                            aria-disabled={!telLink}
                        >
                            {selectedFoodInfo.telephone ? selectedFoodInfo.telephone : '전화번호 없음'}
                        </ActionButton>
                    </ActionRow>

                    <div style={{marginTop: 12, color: '#ddd', whiteSpace: 'pre-wrap'}}>
                        {selectedFoodInfo.description ?? '설명 정보 없음'}
                    </div>
                </Card>

                <Card>
                    <MapWrapper>
                        {!isMapLoaded && !mapError && <Skeleton height="100%" />}
                        {mapError && <div style={{padding: 16}}>{mapError}</div>}
                        <div ref={mapElement} style={{width: '100%', height: '100%'}} />
                    </MapWrapper>
                </Card>
            </InfoGrid>
        </FoodInfoPageWrapper>
    );
};

export default FoodInfoDetailPage;
